import React, { useState, useEffect, useRef } from 'react';
import authService from '../api/authService';
import trainerService from '../api/trainerService';
import './ManagementPages.css';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [error, setError] = useState(null);

  // Use ref to prevent double calls in StrictMode and track if component is mounted
  const hasFetched = useRef(false);
  const isMounted = useRef(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    experience_years: ''
  });

  // Get current user from auth
  const currentUser = authService.getCurrentUser();

  // Load profile data - with proper error handling to prevent loops
  useEffect(() => {
    // Cleanup function to track if component is still mounted
    isMounted.current = true;

    // Prevent multiple calls (especially in React StrictMode)
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Simple approach: just use localStorage data, no API call
    try {
      if (currentUser) {
        // Set profile from currentUser (already in localStorage)
        if (isMounted.current) {
          setProfile(currentUser);
          setFormData({
            name: currentUser.name || '',
            email: currentUser.email || '',
            phone: currentUser.phone || '',
            specialization: currentUser.specialization || '',
            experience_years: currentUser.experience_years || ''
          });
          setLoading(false);
        }
      } else {
        if (isMounted.current) {
          setError('No user logged in');
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      if (isMounted.current) {
        setError('Failed to load profile data');
        setLoading(false);
      }
    }

    // Cleanup function
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Don't proceed if already saving (prevent double-submit)
    if (saving) return;

    setSaving(true);
    setMessage({ type: '', text: '' });
    setError(null);

    try {
      // Only try API update if user has an ID
      if (currentUser && currentUser.id) {
        const response = await trainerService.updateTrainer(currentUser.id, formData);
        if (isMounted.current) {
          setProfile(response.data);
          setIsEditing(false);
          setMessage({ type: 'success', text: 'Profile updated successfully!' });

          // Update user in localStorage
          const updatedUser = { ...currentUser, ...formData };
          localStorage.setItem('user', JSON.stringify(updatedUser));

          setTimeout(() => {
            if (isMounted.current) {
              setMessage({ type: '', text: '' });
            }
          }, 3000);
        }
      } else {
        // Fallback: just update localStorage
        const updatedUser = { ...currentUser, ...formData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        if (isMounted.current) {
          setProfile(updatedUser);
          setIsEditing(false);
          setMessage({ type: 'success', text: 'Profile updated in session!' });
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (isMounted.current) {
        setMessage({
          type: 'error',
          text: `Failed to update profile: ${error.response?.data?.message || error.message || 'Unknown error'}`
        });
      }
    } finally {
      if (isMounted.current) {
        setSaving(false);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      name: profile.name || '',
      email: profile.email || '',
      phone: profile.phone || '',
      specialization: profile.specialization || '',
      experience_years: profile.experience_years || ''
    });
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return (
      <div className="management-page">
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          Loading profile...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-page">
        <div className="page-header">
          <h1 className="page-title">Profile</h1>
        </div>
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: 'var(--danger-color)',
          background: '#fee2e2',
          borderRadius: 'var(--border-radius)',
          border: '1px solid #fecaca',
          margin: '1rem'
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="management-page">
        <div className="page-header">
          <h1 className="page-title">Profile</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-600)' }}>
          Please log in to view your profile.
        </div>
      </div>
    );
  }

  return (
    <div className="management-page fade-in">
      <div className="page-header-compact">
        <div className="header-left">
          <h1 className="page-title">
            <span className="page-title-icon">👤</span> My Profile
          </h1>
          <p className="page-subtitle">View and manage your personal information</p>
        </div>
        {!isEditing && (
          <button
            className="add-btn-primary"
            onClick={() => setIsEditing(true)}
          >
            ✏️ Edit Profile
          </button>
        )}
      </div>

      {message.text && (
        <div
          className={`message-banner ${message.type}`}
          style={{
            padding: '1rem',
            marginBottom: '1.5rem',
            borderRadius: '0.5rem',
            backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`
          }}
        >
          {message.text}
        </div>
      )}

      <div className="profile-container" style={{
        background: 'white',
        borderRadius: 'var(--border-radius)',
        boxShadow: 'var(--shadow-lg)',
        padding: '2rem',
        border: '1px solid var(--gray-200)'
      }}>
        {!isEditing ? (
          // View Mode
          <div className="profile-view">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Name
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {profile?.name || 'N/A'}
                </p>
              </div>

              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Email
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {profile?.email || 'N/A'}
                </p>
              </div>

              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Phone
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {profile?.phone || 'N/A'}
                </p>
              </div>

              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Role
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500',
                  textTransform: 'capitalize'
                }}>
                  {profile?.role || 'N/A'}
                </p>
              </div>

              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Specialization
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {profile?.specialization || 'N/A'}
                </p>
              </div>

              <div className="profile-field">
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem'
                }}>
                  Experience (Years)
                </label>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'var(--gray-900)',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {profile?.experience_years || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Edit Mode
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="form-field">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-field">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Specialization</label>
                <input
                  type="text"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>Experience (Years)</label>
                <input
                  type="number"
                  name="experience_years"
                  value={formData.experience_years}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
